import TileWrapper from '../TileWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useOverviewData } from 'app/frontend/base/config/queries'

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { cn } from '@/lib/utils'
import GearStatus from 'app/frontend/types/custom/GearStatus'
import { useTile } from '../../context'
import { CharacterClassIcon } from 'app/frontend/base/components'
import CharacterLink from 'app/frontend/base/components/CharacterLink'
import { Badge } from '@/components/ui/badge'
import { itemSlotLabels } from 'app/frontend/types/custom/ItemSlot'
import Card from 'app/frontend/base/components/Card'
import { useIsMobile } from '@/hooks/use-mobile'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import pluralize from 'pluralize'

type MetricRowProps = {
  noun: string
  prefix?: string
  suffix?: string
  metric: GearStatus[keyof GearStatus]
}

const MetricRow = ({ noun, prefix, suffix, metric }: MetricRowProps) => {
  const { tile } = useTile()
  const { team } = useTeam()
  const isMobile = useIsMobile()
  const hasCharacters = metric.characters.length > 0

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger
        className={cn('cursor-default', hasCharacters && 'cursor-pointer')}
      >
        <Card className='h-full'>
          <li
            className={
              tile.position === 'main'
                ? 'flex flex-col items-center justify-center gap-1 h-full'
                : ''
            }
          >
            <strong
              className={cn(
                'inline-block mr-1',
                metric.total === 0 ? 'text-s' : 'text-destructive-foreground',
                tile.position === 'main' && 'text-2xl',
              )}
            >
              {metric.total}
            </strong>{' '}
            <span className='text-center'>
              {[prefix, pluralize(noun, metric.total), suffix]
                .filter(Boolean)
                .join(' ')}
            </span>
          </li>
        </Card>
      </HoverCardTrigger>
      {hasCharacters && (
        <HoverCardContent
          side={isMobile || tile.position === 'main' ? 'bottom' : 'left'}
          className='max-w-80 w-fit py-3 max-h-[40vh] overflow-y-auto'
          align='start'
        >
          <ul className='space-y-2'>
            {metric.characters.map((info) => {
              const character = team.members?.find(
                (member) => member.characterReference.id === info.id,
              )?.characterReference

              if (!character) return null

              return (
                <li
                  key={info.id}
                  className='flex items-center justify-between gap-4'
                >
                  <CharacterLink
                    className='flex items-center gap-1.5'
                    character={character}
                  >
                    <CharacterClassIcon
                      characterClass={character.characterClass}
                      size='small'
                    />
                    {character.name}
                  </CharacterLink>
                  <span className='text-muted-foreground flex flex-wrap gap-1.5'>
                    {info.slots ? (
                      <Badge variant='outline' className='font-normal'>
                        {info.slots.length > 1
                          ? `${info.slots.length} items`
                          : itemSlotLabels[
                              info.slots[0] as keyof typeof itemSlotLabels
                            ]}
                      </Badge>
                    ) : info.count ? (
                      <Badge variant='outline' className='font-normal'>
                        {info.count}
                      </Badge>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>
        </HoverCardContent>
      )}
    </HoverCard>
  )
}

const EnchantAuditTile = () => {
  const gameVersion = useGameVersion()
  const { team } = useTeam()
  const { tile } = useTile()
  const { data: overviewData } = useOverviewData(team.id)
  const enchantData = overviewData?.enchantData as GearStatus | undefined

  if (!enchantData) {
    return (
      <TileWrapper>
        <p className='text-muted-foreground'>No enchant data available.</p>
      </TileWrapper>
    )
  }

  return (
    <TileWrapper>
      <ul
        className={cn(
          'gap-2',
          tile.position === 'main' ? 'grid grid-cols-4' : 'flex flex-col',
        )}
      >
        {tile.config.leniency === 'strict' ? (
          <>
            <MetricRow
              noun='enchant'
              suffix='missing'
              metric={enchantData.enchants_missing}
            />
            {gameVersion.has('gear_quality_audit') ? (
              <MetricRow
                prefix='poor quality'
                noun='enchant'
                metric={enchantData.enchants_not_bis}
              />
            ) : null}
          </>
        ) : (
          <MetricRow
            noun='enchant'
            suffix='missing'
            metric={enchantData.enchants_missing}
          />
        )}

        {gameVersion.has('gear_quality_audit') ? (
          <MetricRow
            prefix='empty'
            noun='socket'
            metric={enchantData.empty_sockets}
          />
        ) : null}

        {gameVersion.has('retail_gear_audit') ? (
          <MetricRow
            noun='embellishment'
            suffix='missing'
            metric={enchantData.embellishments}
          />
        ) : null}
      </ul>
    </TileWrapper>
  )
}

export default EnchantAuditTile
